import Cell from "src/models/cell";
import type {
  CellBulkUpdatePayload,
  CellCreatePayload,
  CellLitePayload,
  CellPayload,
  CellStructurePatch,
} from "src/types";
import Repository from "./repository";

const resource = "cells";

// Cells are pure structure now: GET carries no crops. The only crop-bearing shape
// is the optimization WebSocket snapshot (a single `crop_id`), wrapped here into one
// dateless placement so the live view can render it. The plan's real crops come from
// the /placements/ resource, merged in by `useGardenCells`.
export const payloadToGrid = (data: (CellPayload | CellLitePayload)[]): Cell[][] => {
  if (!data || data.length === 0) return [];
  const cols = Math.max(...data.map((c) => c.x)) + 1;
  const flat = data.map((c) => {
    const cell = new Cell(c.type, null, c.greenhouse_height, c.height, c.sun_exposure ?? {});
    cell.id = "id" in c && typeof c.id === "number" ? c.id : null;
    const cropId = (c as CellLitePayload).crop_id;
    cell.placements = cropId != null ? [{ cropId, startOn: null, endOn: null, isLocked: false }] : [];
    return cell;
  });
  const grid: Cell[][] = [];
  while (flat.length > 0) grid.push(flat.splice(0, cols));
  return grid;
};

export default {
  async list(gardenId: number): Promise<Cell[][]> {
    const { data } = await Repository.get(`/gardens/${gardenId}/${resource}/`);
    return payloadToGrid(data);
  },
  // POST replaces the whole plan's STRUCTURE and returns every cell recomputed
  // (ids + sun_exposure). Crops are untouched (they live in /placements/).
  async replace(gardenId: number, cells: CellCreatePayload[]): Promise<Cell[][]> {
    const { data } = await Repository.post(`/gardens/${gardenId}/${resource}/`, cells);
    return payloadToGrid(data);
  },
  // Apply the same structure change (a brush gesture) to several cells at once.
  // Omitted patch fields are left unchanged; the backend also fixes up placements
  // on the updated cells (e.g. drops crops when a cell becomes non-arable).
  bulkUpdate(gardenId: number, cellIds: number[], patch: CellStructurePatch) {
    const body: CellBulkUpdatePayload = { cell_ids: cellIds, ...patch };
    return Repository.post(`/gardens/${gardenId}/${resource}/bulk-update/`, body);
  },
  // Grow/shrink the grid by one edge band. The backend renumbers the surviving
  // cells' (x,y) while KEEPING their ids (so placements are preserved), added cells
  // inherit the neighbouring row/column's type, and it recomputes sun-exposure.
  // Returns the whole garden (ordered y, x). Use this — not `replace` — for resize.
  async resize(
    gardenId: number,
    edge: "top" | "bottom" | "left" | "right",
    action: "add" | "remove",
    count = 1
  ): Promise<Cell[][]> {
    const { data } = await Repository.post(`/gardens/${gardenId}/${resource}/resize/`, { edge, action, count });
    return payloadToGrid(data);
  },
};
